import React, { useState, useEffect, useRef, useMemo } from 'react';
import JoditEditor from 'jodit-react';
import { FiX, FiCheck, FiBriefcase, FiTrash2 } from 'react-icons/fi';
import { toast } from 'react-toastify';
import SignatureCanvas from 'react-signature-canvas';
import { Modal, Input, Button, Select } from '../../components';
import '../../components/CompanyForm/style.css';
import './style.css';

export default function SpecificDocumentModal({ isOpen, onClose, onSave, initialData, companies = [] }) {
  const [title, setTitle] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [content, setContent] = useState('');
  const editorRef = useRef(null);
  const signatureRef = useRef(null);
  const [showSignature, setShowSignature] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setTitle(initialData.title || '');
        setCompanyId(initialData.company_id ? String(initialData.company_id) : '');
        setContent(initialData.content || '');
      } else {
        setTitle('');
        setCompanyId('');
        setContent('');
      }
    }
  }, [isOpen, initialData]);

  useEffect(() => {
    if (content && /{{\s*assinatura_empresa\s*}}/i.test(content)) {
      setShowSignature(true);
    } else {
      setShowSignature(false);
    }
  }, [content]);

  useEffect(() => {
      // Debug log para ajudar a entender o fluxo
      console.log('Verificando assinatura:', { 
        showSignature, 
        hasInitialSignature: !!(initialData && initialData.signature), 
        canvasRef: !!signatureRef.current 
      });

      if (showSignature && initialData?.signature && signatureRef.current) {
          const loadSignature = () => {
            try {
              if (signatureRef.current) {
                // Limpa antes de carregar para evitar sobreposição se chamado múltiplas vezes
                signatureRef.current.clear();
                signatureRef.current.fromDataURL(initialData.signature);
              }
            } catch (error) {
              console.error("Erro ao carregar assinatura:", error);
            }
          };

          // Tenta carregar com um pequeno delay para garantir que o modal/canvas esteja renderizado
          // Usando requestAnimationFrame para sincronizar com a renderização
          requestAnimationFrame(() => {
            setTimeout(loadSignature, 100);
          });
      }
  }, [showSignature, initialData]);

  const clearSignature = () => {
    if (signatureRef.current) {
        signatureRef.current.clear();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error('O título é obrigatório');
      return;
    }
    if (!companyId) {
      toast.error('A empresa é obrigatória');
      return;
    }

    let signatureData = null;
    if (showSignature && signatureRef.current && !signatureRef.current.isEmpty()) {
        signatureData = signatureRef.current.toDataURL();
    }

    onSave({ 
        title, 
        content, 
        html: content,
        company_id: companyId, 
        signature: signatureData 
    });
  };

  const insertVariable = (variable) => {
    try {
      if (editorRef.current) {
        // Jodit instance
        const editor = editorRef.current;
        
        // Garante que o editor tem foco antes de inserir
        // Isso ajuda a prevenir erros de seleção perdida
        if (document.activeElement !== editor.editor) {
          editor.focus();
        }

        // Verifica se a seleção é válida antes de tentar inserir
        if (editor.selection) {
            editor.selection.insertHTML(`{{${variable}}} `);
        } else {
            // Fallback: insere no final se não conseguir usar a seleção
            editor.value += ` {{${variable}}} `;
        }
        
        // Força atualização do estado para detectar mudanças (ex: assinatura) imediatamente
        setContent(editor.value);

      } else {
        toast.warn('Aguarde o editor carregar completamente.');
      }
    } catch (error) {
      console.error('Erro ao inserir variável:', error);
      toast.error('Erro ao inserir variável. Tente clicar no editor antes de selecionar a variável.');
    }
  };

  const config = useMemo(() => ({
    readonly: false,
    height: 500,
    language: 'pt_br',
    toolbarSticky: false,
    buttons: [
      'source', '|',
      'bold', 'strikethrough', 'underline', 'italic', '|',
      'ul', 'ol', '|',
      'outdent', 'indent',  '|',
      'font', 'fontsize', 'brush', 'paragraph', '|',
      'image', 'table', 'link', '|',
      'align', 'undo', 'redo', '|',
      'hr', 'eraser', 'copyformat', '|',
      'fullsize', 'selectall', 'print'
    ],
    uploader: {
      insertImageAsBase64URI: true
    },
    removeButtons: ['about'],
    showCharsCounter: false,
    showWordsCounter: false,
    showXPathInStatusbar: false,
    placeholder: 'Escreva o conteúdo do documento aqui...',
    enableDragAndDropFileToEditor: true,
    askBeforePasteFromWord: false,
    askBeforePasteHTML: false,
    defaultActionOnPaste: 'insert_as_html',
  }), []);

  const companyOptions = useMemo(() => {
    return [
      { value: '', label: 'SELECIONE UMA EMPRESA...' },
      ...companies.map(c => ({
        value: String(c.id),
        label: c.corporate_name || c.trade_name || `Empresa ${c.id}`
      }))
    ];
  }, [companies]);

  const variableGroups = [
    {
      title: 'Empresa',
      items: [
        { label: 'Nome da Empresa', value: 'NOME_EMPRESA' },
        { label: 'Representante', value: 'REPRESENTANTE_EMPRESA' },
        { label: 'Assinatura', value: 'ASSINATURA_EMPRESA' },
        { label: 'Data Atual', value: 'DATA_ATUAL' },
      ]
    },
    {
      title: 'Funcionário',
      items: [
        { label: 'Nome', value: 'NOME_FUNCIONARIO' },
        { label: 'Endereço', value: 'ENDERECO_EMPREGADO' },
        { label: 'Nome do Pai', value: 'NOME_PAI' },
        { label: 'Nome da Mãe', value: 'NOME_MAE' },
        { label: 'Data Nascimento', value: 'DATA_NASCIMENTO' },
        { label: 'Nome do Cônjuge', value: 'NOME_CONJUGE' },
        { label: 'Categoria', value: 'CATEGORIA' },
        { label: 'Título Eleitoral', value: 'TITULO_ELEITORAL' },
        { label: 'Zona Eleitoral', value: 'ZONA_ELEITORAL' },
        { label: 'Seção Eleitoral', value: 'SECAO_ELEITORAL' },
        { label: 'Data da Opção', value: 'DATA_OPCAO' },
        { label: 'RNE', value: 'RNE' },
        { label: 'Casado com Brasileiro', value: 'CASADO_BRASILEIRO' },
        { label: 'Cargo', value: 'CARGO' },
        { label: 'Profissão Regul. DRT', value: 'PROFISSAO_DRT' },
        { label: 'Nº de Registro DRT', value: 'REGISTRO_DRT' },
        { label: 'Tipo Sanguíneo e Rh', value: 'TIPO_SANGUINEO' },
        { label: 'Data da Admissão', value: 'DATA_ADMISSAO' },
        { label: 'PIS/PASEP', value: 'PIS_PASEP' },
        { label: 'Horário de Trabalho', value: 'JORNADA_FUNCIONARIO' },
        { label: 'Nacionalidade', value: 'NACIONALIDADE' },
        { label: 'Estado Civil', value: 'ESTADO_CIVIL' },
        { label: 'Órgão Expedidor', value: 'ORGAO_EXPEDIDOR' },
        { label: 'Naturalidade', value: 'NATURALIDADE' },
        { label: 'Série/UF', value: 'SERIE_UF' },
        { label: 'Identidade', value: 'IDENTIDADE' },
        { label: 'Data Exp. Identidade', value: 'DATA_EXPEDICAO_IDENTIDADE' },
        { label: 'Grau de Instrução', value: 'GRAU_INSTRUCAO' },
        { label: 'CTPS', value: 'CTPS' },
        { label: 'Data Exp. CTPS', value: 'DATA_EXPEDICAO_CTPS' },
        { label: 'Data da Chegada', value: 'DATA_CHEGADA' },
        { label: 'CPF', value: 'CPF_FUNCIONARIO' },
        { label: 'Data da Naturalização', value: 'DATA_NATURALIZACAO' },
        { label: 'Portaria Naturalização', value: 'PORTARIA_NATURALIZACAO' },
        { label: 'É Naturalizado', value: 'NATURALIZADO' },
        { label: 'Tem Filho(a) Brasil', value: 'FILHO_BRASIL' },
        { label: 'Lotação', value: 'LOTACAO' },
        { label: 'CBO', value: 'CBO' },
        { label: 'Data Últ. Reciclagem', value: 'DATA_RECICLAGEM' },
        { label: 'Data Form. Prof.', value: 'DATA_FORMACAO' },
        { label: 'Nº da Folha DRT', value: 'FOLHA_DRT' },
        { label: 'Nº do Livro DRT', value: 'LIVRO_DRT' },
        { label: 'Pessoa c/ Deficiência', value: 'DEFICIENCIA' },
        { label: 'Salário Variável', value: 'SALARIO_VARIAVEL' },
        { label: 'Forma de Pagto', value: 'FORMA_PAGAMENTO' },
        { label: 'Salário', value: 'SALARIO' },
        { label: 'Celular', value: 'CELULAR_FUNCIONARIO' },
        { label: 'Email', value: 'EMAIL_FUNCIONARIO' },
        { label: 'Assinatura', value: 'ASSINATURA_FUNCIONARIO' },
        { label: 'Sexo', value: 'SEXO_FUNCIONARIO' },
        { label: 'Tipo Contrato', value: 'TIPO_CONTRATO' },
        { label: 'Status', value: 'STATUS_FUNCIONARIO' },
        { label: 'Matrícula', value: 'MATRICULA' },
        { label: 'Home Office', value: 'HOMEOFFICE' },
        { label: 'Horário Início', value: 'HORARIO_INICIO' },
        { label: 'Últimas Férias', value: 'ULTIMAS_FERIAS' },
        { label: 'Altura', value: 'ALTURA' },
        { label: 'Peso', value: 'PESO' },
        { label: 'Tipo Deficiência', value: 'TIPO_DEFICIENCIA' },
        { label: 'Banco', value: 'BANCO_NOME' },
        { label: 'Agência', value: 'BANCO_AGENCIA' },
        { label: 'Conta', value: 'BANCO_CONTA' },
        { label: 'Tipo Conta', value: 'BANCO_TIPO_CONTA' },
        { label: 'Tem PIX', value: 'TEM_PIX' },
        { label: 'Tipo Chave PIX', value: 'PIX_TIPO' },
        { label: 'Chave PIX', value: 'PIX_CHAVE' },
        { label: 'Salário Família', value: 'SALARIO_FAMILIA' },
        { label: 'Nº Dependentes', value: 'NUM_DEPENDENTES' },
      ]
    },
    {
      title: 'Posto de Serviço',
      items: [
        { label: 'Nome do Posto', value: 'POSTO_SERVICO' },
        { label: 'Endereço', value: 'ENDERECO_POSTO' },
      ]
    },
    {
      title: 'Cliente',
      items: [
        { label: 'Nome', value: 'NOME_CLIENTE' },
        { label: 'Documento', value: 'DOCUMENTO_CLIENTE' },
        { label: 'Endereço', value: 'ENDERECO_CLIENTE' },
      ]
    },
    {
      title: 'Fornecedor',
      items: [
        { label: 'Nome', value: 'NOME_FORNECEDOR' },
        { label: 'Documento', value: 'DOCUMENTO_FORNECEDOR' },
      ]
    },
    {
      title: 'Contador',
      items: [
        { label: 'Nome', value: 'NOME_CONTADOR' },
        { label: 'CRC', value: 'CRC_CONTADOR' },
        { label: 'Email', value: 'EMAIL_CONTADOR' },
      ]
    }
  ];

  return (
    <Modal
      closeOnOverlayClick={false}
      isOpen={isOpen}
      onClose={onClose}
      title={initialData ? "Editar Documento" : "Novo Documento Específico"}
      size="xl"
    >
      <form onSubmit={handleSubmit} className="company-form-component">
        <div className="form-body">
          <div className="form-grid">
            <div className="full-width">
              <Select
                label="Empresa *"
                searchable
                options={companyOptions}
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
                placeholder="SELECIONE UMA EMPRESA..."
                icon={FiBriefcase}
                required
              />
            </div>

            <div className="full-width">
              <Input
                label="Título do Documento *"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ex: Termo de Responsabilidade"
                required
              />
            </div>

            <div className="full-width">
              <label className="field-label">Variáveis Disponíveis</label>
              <div className="variables-container" style={{ maxHeight: '200px', overflowY: 'auto', padding: '1rem', border: '1px solid var(--border-color)', borderRadius: '4px', background: '#f9fafb' }}>
                {variableGroups.map((group, index) => (
                  <div key={group.title} style={{ marginBottom: index === variableGroups.length - 1 ? 0 : '1rem' }}>
                    <h4 style={{ 
                      fontSize: '0.85rem', 
                      fontWeight: '600', 
                      color: 'var(--text-secondary)', 
                      marginBottom: '0.5rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      {group.title}
                    </h4>
                    <div className="variables-list">
                      {group.items.map((v) => (
                        <button
                          key={v.value}
                          type="button"
                          className="variable-tag"
                          onClick={() => insertVariable(v.value)}
                          title={`Inserir ${v.label}`}
                        >
                          {v.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <small className="text-secondary" style={{ marginTop: '0.5rem', display: 'block' }}>
                Clique em uma variável para inseri-la no texto.
              </small>
            </div>

            <div className="full-width">
              <label className="field-label">Conteúdo do Documento</label>
              <div className="jodit-wrapper">
                <JoditEditor
                  ref={editorRef}
                  value={content}
                  config={config}
                  onBlur={newContent => setContent(newContent)}
                  onChange={() => {}} // Performance optimization: use onBlur for heavy updates
                />
              </div>
            </div>

            {showSignature && (
                <div className="full-width">
                    <label className="signature-label">Assinatura da Empresa</label>
                    <div className="signature-container">
                        <SignatureCanvas
                            ref={signatureRef}
                            penColor="black"
                            canvasProps={{
                                className: 'signature-pad'
                            }}
                        />
                        <div className="signature-actions">
                            <button 
                                type="button" 
                                className="clear-signature-btn"
                                onClick={clearSignature}
                            >
                                <FiTrash2 size={14} />
                                Limpar Assinatura
                            </button>
                        </div>
                    </div>
                    <small className="text-secondary" style={{ marginTop: '0.5rem', display: 'block' }}>
                        Desenhe a assinatura da empresa acima. Esta assinatura será vinculada a este documento específico.
                    </small>
                </div>
            )}
          </div>
        </div>

        <div className="modal-actions">
          <Button
            type="button"
            className="cancel-button"
            onClick={onClose}
          >
            Cancelar
          </Button>
          <Button type="submit">
            <FiCheck />
            Salvar
          </Button>
        </div>
      </form>
    </Modal>
  );
}
